import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../components/Modal"; // Import the Modal component
import './Gallery.css'; // Import the CSS file for styling

function Gallery() {
  const { t } = useTranslation();
  const [media, setMedia] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCaptions, setExpandedCaptions] = useState({});
  const accessToken = process.env.REACT_APP_INSTAGRAM_ACCESS_TOKEN;

  useEffect(() => {
    document.title = 'Gallery | Sapium Beauty - Treatment Results & Before/After East London';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'See real before and after photos from Sapium Beauty in East London (Canning Town). View results from laser hair removal, chemical peels, PRX-33, BioRePeel and other advanced skin treatments.'
      );
    }
  }, []);

  useEffect(() => {
    const fetchInstagramMedia = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!accessToken) {
          console.error("Instagram access token is missing!");
          throw new Error(t('gallery.errors.tokenMissing'));
        }
        
        console.log("Fetching Instagram media with token:", accessToken ? `${accessToken.substring(0, 10)}...` : 'missing');
        
        const response = await fetch(`https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,thumbnail_url&access_token=${accessToken}`);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Instagram API error:", response.status, errorData);
          throw new Error(`${t('gallery.errors.apiError')}: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
        }
        
        const data = await response.json();
        console.log("Instagram API response:", data);
        
        if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
          console.log(`Successfully loaded ${data.data.length} Instagram posts`);
          setMedia(data.data);
        } else if (data && data.data && Array.isArray(data.data) && data.data.length === 0) {
          console.warn("Instagram API returned empty data array");
          setError(t('gallery.errors.noPosts'));
        } else {
          console.error("No media found or error in response:", data);
          setError(t('gallery.errors.noPosts'));
        }
      } catch (error) {
        console.error("Error fetching Instagram media:", error);
        setError(error.message || t('gallery.errors.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramMedia();
  }, [accessToken, t]);

  const openModal = (imageUrl, caption) => {
    setSelectedImage({ imageUrl, caption });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const toggleCaption = (id) => {
    setExpandedCaptions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="gallery-page">
      <section className="gallery-hero">
        <div className="container">
          <h1>{t('gallery.hero.title')}</h1>
          <p>{t('gallery.hero.description')}</p>
        </div>
      </section>

      <section className="gallery-section">
        <div className="container">
          {loading ? (
            <div className="loading-container">
              <p>{t('gallery.loading')}</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>{error}</p>
              <p>{t('gallery.error')}</p>
            </div>
          ) : (
            <div className="gallery-grid">
              {media && Array.isArray(media) && media.length > 0 ? media.map((item, index) => (
                <div
                  key={index}
                  className="gallery-item"
                  onClick={() => item.media_type === "IMAGE" && openModal(item.media_url, item.caption)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && item.media_type === "IMAGE" && openModal(item.media_url, item.caption)}
                >
                  {item.media_type === "IMAGE" ? (
                    <img 
                      src={item.media_url} 
                      alt={item.caption || t('gallery.accessibility.instagramPost')} 
                      className="gallery-image"
                      loading="lazy"
                    />
                  ) : item.media_type === "VIDEO" ? (
                    <div className="video-container">
                      <video 
                        controls 
                        className="gallery-video"
                        poster={item.thumbnail_url}
                        loading="lazy"
                      >
                        <source src={item.media_url} type="video/mp4" />
                        {t('gallery.accessibility.videoNotSupported')}
                      </video>
                    </div>
                  ) : null}
                  {item.caption && (
                    <div className="gallery-caption">
                      <p className={expandedCaptions[index] ? 'expanded' : ''}>
                        {item.caption}
                      </p>
                      {item.caption && item.caption.length > 100 && (
                        <button 
                          className="read-more-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCaption(index);
                          }}
                        >
                          {expandedCaptions[index] ? t('gallery.buttons.showLess') : t('gallery.buttons.readMore')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )) : (
                <p>{t('gallery.errors.noPosts')}</p>
              )}
            </div>
          )}
        </div>
      </section>

      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        imageUrl={selectedImage?.imageUrl} 
        caption={selectedImage?.caption} 
      />
    </div>
  );
}

export default Gallery;
